import React, { useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Alert,
  TextField,
  Modal,
  Box as MuiBox,
  Paper,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getPlainTextFromHtml } from './udyogconnect2.0/utils/richHtml';

const buildEventDetailsUrl = (eventId) =>
  `${window.location.origin}/2.0/calendar?event=${encodeURIComponent(`event-${eventId}`)}`;

const buildEventRegistrationUrl = (eventId) =>
  `${window.location.origin}/2.0/event/${encodeURIComponent(eventId)}/register`;

const buildEventImageUrl = (imageId) =>
  `${process.env.REACT_APP_API_BASE_URL}/api/admin/events/images/${imageId}`;

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  window.prompt('Copy this share link:', value);
};

const compactText = (value) => getPlainTextFromHtml(value).replace(/\s+/g, ' ').trim();

const buildEventTitle = (description) => {
  const cleaned = compactText(description);
  if (!cleaned) return 'Untitled event';
  const firstSentence = cleaned.split(/[.!?]/)[0] || cleaned;
  const title = firstSentence.length >= 22 ? firstSentence : cleaned;
  return title.length > 88 ? `${title.slice(0, 85).trim()}…` : title;
};

const defaultPaymentSettings = {
  enabled: false,
  amountUsd: '',
  label: 'General Admission',
  addToCartEnabled: true,
  note: 'Payment collection is not enabled yet. UC will follow up if payment is required.',
};

const emptyEventForm = {
  eventTitle: '',
  eventCategory: 'Udyog Connect Event',
  eventDate: '',
  description: '',
  venue: '',
  link: '',
  capacity: '',
  nativeRegistrationEnabled: true,
  registrationFields: [],
  paymentSettings: defaultPaymentSettings,
  startTime: '',
  endTime: '',
  status: 'open',
  publicationStatus: 'draft',
  whoCanApply: 'anyone',
};

const registrationFieldTypes = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'textarea', label: 'Long text' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
];

const makeRegistrationField = () => ({
  key: `custom_${Date.now()}`,
  label: '',
  type: 'text',
  required: false,
  placeholder: '',
  options: [],
  optionsText: '',
});

const normalizeFieldForForm = (field = {}) => ({
  ...field,
  key: field.key || `custom_${Date.now()}`,
  label: field.label || '',
  type: field.type || 'text',
  required: Boolean(field.required),
  placeholder: field.placeholder || '',
  options: Array.isArray(field.options) ? field.options : [],
  optionsText: Array.isArray(field.options) ? field.options.join('\n') : String(field.options || ''),
});

const normalizeFieldsForSave = (fields = []) =>
  fields
    .map((field) => ({
      key: field.key,
      label: String(field.label || '').trim(),
      type: field.type || 'text',
      required: Boolean(field.required),
      placeholder: String(field.placeholder || '').trim(),
      options: String(field.optionsText || '')
        .split(/\n|,/)
        .map((option) => option.trim())
        .filter(Boolean),
    }))
    .filter((field) => field.label);

const normalizePaymentForSave = (settings = {}) => ({
  enabled: Boolean(settings.enabled),
  amountUsd: settings.enabled ? Number(settings.amountUsd || 0) : 0,
  label: String(settings.label || 'General Admission').trim(),
  addToCartEnabled: Boolean(settings.addToCartEnabled),
  note: String(settings.note || '').trim(),
});

const getDateValue = (rawDate) => {
  const date = rawDate ? new Date(rawDate).getTime() : 0;
  return Number.isNaN(date) ? 0 : date;
};

const Events = () => {
  const token = localStorage.getItem('token');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [isAdmin] = useState(true); // Assuming admin status is handled
  const [editingEvent, setEditingEvent] = useState(null);
  const [open, setOpen] = useState(false); // Modal state for Add/Edit
  const [sortField, setSortField] = useState('eventDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [lastSavedShareUrl, setLastSavedShareUrl] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [savingEvent, setSavingEvent] = useState(false);
  const [eventForm, setEventForm] = useState(emptyEventForm);

  useEffect(() => {
    // Fetching upcoming events
    fetchevents()
  }, []);

  const fetchEventImages = async (eventId) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/events/${eventId}/images`);
      return (Array.isArray(res.data) ? res.data : []).map((image) => ({
        id: image.id,
        url: buildEventImageUrl(image.id),
      }));
    } catch (error) {
      console.warn('Unable to load event images', eventId, error);
      return [];
    }
  };

  const fetchevents= () => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/events?includeDrafts=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const enriched = await Promise.all(
          rows.map(async (event) => ({
            ...event,
            images: await fetchEventImages(event.id),
          }))
        );
        setEvents(enriched);
      })
      .catch(err => {
        console.error('Error fetching events', err);
        setNotice({ type: 'error', message: err?.response?.data?.message || 'Unable to load events.' });
      })
      .finally(() => setLoading(false));
  };


  const handleDeleteEvent = (id) => {
    if (!window.confirm('Delete this event?')) return;
    axios
      .delete(`${process.env.REACT_APP_API_BASE_URL}/api/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setEvents(events.filter((event) => event.id !== id));
        setNotice({ type: 'success', message: 'Event deleted.' });
      })
      .catch((err) => {
        console.error('Error deleting event', err);
        setNotice({ type: 'error', message: err?.response?.data?.message || 'Unable to delete event.' });
      });
  };

  const handleEditEvent = (event) => {
    const formattedDate = new Date(event.eventDate).toISOString().split('T')[0]; // "YYYY-MM-DD"
    setEditingEvent(event);
    setLastSavedShareUrl('');
    setNotice({ type: '', message: '' });
    setSelectedImages([]);
    setExistingImages(event.images || []);
    setEventForm({
      ...emptyEventForm,
      eventDate: formattedDate,
      eventTitle: event.eventTitle || buildEventTitle(event.description),
      eventCategory: event.eventCategory || 'Udyog Connect Event',
      description: event.description || '',
      venue: event.venue || '',
      link: event.externalRegistrationUrl || event.link || '',
      capacity: event.capacity || '',
      nativeRegistrationEnabled: event.nativeRegistrationEnabled !== false,
      registrationFields: (event.registrationFields || []).map(normalizeFieldForForm),
      paymentSettings: {
        ...defaultPaymentSettings,
        ...(event.paymentSettings || {}),
        amountUsd: event.paymentSettings?.amountUsd || '',
        enabled: Boolean(event.paymentSettings?.enabled),
        addToCartEnabled: event.paymentSettings?.addToCartEnabled !== false,
      },
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      status:event.status || 'open',
      publicationStatus: event.publicationStatus || 'published',
      whoCanApply:event.whoCanApply || 'anyone'
    });
    setOpen(true);
  };
  

  const handleAddEvent = () => {
    setEditingEvent(null); // Clear any selected event
    setLastSavedShareUrl('');
    setNotice({ type: '', message: '' });
    setSelectedImages([]);
    setExistingImages([]);
    setEventForm(emptyEventForm);
    setOpen(true);
  };

  const uploadSelectedEventImages = async (eventId) => {
    if (!eventId || selectedImages.length === 0) return;
    const formData = new FormData();
    selectedImages.forEach((file) => formData.append('images', file));
    await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/admin/events/${eventId}/upload-images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const deleteExistingImage = async (imageId) => {
    if (!editingEvent?.id || !imageId) return;
    if (!window.confirm('Delete this event image?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/events/${editingEvent.id}/images/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExistingImages((current) => current.filter((image) => image.id !== imageId));
      setNotice({ type: 'success', message: 'Event image deleted.' });
      fetchevents();
    } catch (error) {
      console.error('Error deleting event image', error);
      setNotice({ type: 'error', message: error?.response?.data?.message || 'Unable to delete image.' });
    }
  };

  const selectedImagePreviews = selectedImages.map((file) => ({
    name: file.name,
    url: URL.createObjectURL(file),
  }));

  const updateRegistrationField = (index, key, value) => {
    setEventForm((current) => ({
      ...current,
      registrationFields: current.registrationFields.map((field, fieldIndex) =>
        fieldIndex === index ? { ...field, [key]: value } : field
      ),
    }));
  };

  const addRegistrationField = () => {
    setEventForm((current) => ({
      ...current,
      registrationFields: [...current.registrationFields, makeRegistrationField()],
    }));
  };

  const removeRegistrationField = (index) => {
    setEventForm((current) => ({
      ...current,
      registrationFields: current.registrationFields.filter((_, fieldIndex) => fieldIndex !== index),
    }));
  };

  const updatePaymentSetting = (key, value) => {
    setEventForm((current) => ({
      ...current,
      paymentSettings: {
        ...current.paymentSettings,
        [key]: value,
      },
    }));
  };

  const handleFormSubmit = async () => {
    const { eventTitle, eventDate, description, venue,startTime, endTime,status,whoCanApply} = eventForm;
    const plainDescription = getPlainTextFromHtml(description);

    if (!eventTitle || !eventDate || !startTime || !endTime || !plainDescription || !venue || !status || !whoCanApply) {
      setNotice({ type: 'error', message: 'Event title, rich content, date, time, venue, status, and audience are mandatory.' });
      return;
    }

    if (eventForm.paymentSettings.enabled && Number(eventForm.paymentSettings.amountUsd || 0) <= 0) {
      setNotice({ type: 'error', message: 'Paid registration needs a dollar amount greater than zero.' });
      return;
    }

    const payload = {
      ...eventForm,
      capacity: eventForm.capacity === '' ? null : Number(eventForm.capacity),
      registrationFields: normalizeFieldsForSave(eventForm.registrationFields),
      paymentSettings: normalizePaymentForSave(eventForm.paymentSettings),
    };

    setSavingEvent(true);
    try {
      const response = editingEvent
        ? await axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/events/${editingEvent.id}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        : await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/events`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );

      const savedId = response.data?.id || response.data?.event?.id || editingEvent?.id;
      await uploadSelectedEventImages(savedId);

      if (savedId && payload.publicationStatus === 'published') {
        setLastSavedShareUrl(response.data?.registrationUrl || buildEventRegistrationUrl(savedId));
      }
      setNotice({
        type: 'success',
        message: payload.publicationStatus === 'published'
          ? `${editingEvent ? 'Event updated' : 'Event added'} successfully${selectedImages.length ? ' with images' : ''}. It is published and the share link is available below.`
          : `${editingEvent ? 'Event updated' : 'Event added'} successfully as a draft${selectedImages.length ? ' with images' : ''}. Publish it when it is ready for visitors.`,
      });
      setOpen(false);
      setSelectedImages([]);
      setExistingImages([]);
      fetchevents();
    } catch (err) {
      console.error(editingEvent ? 'Error updating event' : 'Error adding event', err);
      setNotice({ type: 'error', message: err?.response?.data?.message || 'Unable to save event.' });
    } finally {
      setSavingEvent(false);
    }
  };
  
  const formatEventDate = (rawDate) => {
    if (!rawDate) return 'Date TBA';
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return 'Date TBA';
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCompactDate = (rawDate) => {
    if (!rawDate) return 'Date TBA';
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return 'Date TBA';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeRange = (event) => {
    const start = event.startTime || 'TBA';
    const end = event.endTime || 'TBA';
    return `${start} - ${end}`;
  };

  const openRegistration = (link) => {
    if (!link) {
      alert('Registration link is not available for this event.');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const isRegistrationOpen = (event) =>
    (event.publicationStatus || 'published') === 'published' &&
    String(event.status || '').toLowerCase() === 'open';
  const getRegistrationUrl = (event) =>
    (event.publicationStatus || 'published') === 'published'
      ? (event.registrationUrl || (event.nativeRegistrationEnabled === false ? '' : buildEventRegistrationUrl(event.id)))
      : '';

  const filteredEvents = events.filter((event) => {
    const title = event.eventTitle || buildEventTitle(event.description);
    return (
      title.toLowerCase().includes(filter.toLowerCase()) ||
      (event.eventCategory || '').toLowerCase().includes(filter.toLowerCase()) ||
      compactText(event.description || '').toLowerCase().includes(filter.toLowerCase()) ||
      (event.venue || '').toLowerCase().includes(filter.toLowerCase()) ||
      (event.whoCanApply || '').toLowerCase().includes(filter.toLowerCase()) ||
      (event.status || '').toLowerCase().includes(filter.toLowerCase())
    );
  });

  const sortedEvents = [...filteredEvents].sort((first, second) => {
    let firstValue;
    let secondValue;

    if (sortField === 'eventDate') {
      firstValue = getDateValue(first.eventDate);
      secondValue = getDateValue(second.eventDate);
    } else if (sortField === 'startTime') {
      firstValue = String(first.startTime || '');
      secondValue = String(second.startTime || '');
    } else if (sortField === 'eventTitle') {
      firstValue = String(first.eventTitle || buildEventTitle(first.description) || '').toLowerCase();
      secondValue = String(second.eventTitle || buildEventTitle(second.description) || '').toLowerCase();
    } else {
      firstValue = String(first[sortField] || '').toLowerCase();
      secondValue = String(second[sortField] || '').toLowerCase();
    }

    if (firstValue < secondValue) return sortDirection === 'asc' ? -1 : 1;
    if (firstValue > secondValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const openEventsCount = events.filter(isRegistrationOpen).length;
  const memberEventsCount = events.filter((event) => String(event.whoCanApply || '').toLowerCase() === 'member').length;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        mx: { xs: -2, sm: -3, md: -4 },
        my: { xs: -2, sm: -3, md: -4 },
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 12% 12%, rgba(20, 184, 166, 0.16), transparent 30%), radial-gradient(circle at 88% 5%, rgba(37, 99, 235, 0.13), transparent 28%), linear-gradient(135deg, #f8fafc 0%, #eef7f6 48%, #f8fafc 100%)',
      }}
    >
    <Container maxWidth="lg" disableGutters>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.45fr 1fr' },
          gap: 2.5,
          alignItems: 'stretch',
          mb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 4,
            border: '1px solid rgba(15, 118, 110, 0.14)',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Chip
            label="Udyog Connect Events"
            sx={{
              mb: 2,
              bgcolor: '#e6fffb',
              color: '#0f766e',
              border: '1px solid #99f6e4',
              fontWeight: 800,
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{ color: '#0f172a', fontWeight: 900, lineHeight: 1.08, mb: 1.5 }}
          >
            Join learning sessions, community meets, and growth programs.
          </Typography>
          <Typography sx={{ color: '#475569', maxWidth: 760, lineHeight: 1.7 }}>
            Explore upcoming Udyog Connect events, check eligibility, and register for programs
            that help you learn, network, and move from interest to action.
          </Typography>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', lg: '1fr' },
            gap: 1.5,
          }}
        >
          {[
            { label: 'Total events', value: events.length },
            { label: 'Open now', value: openEventsCount },
            { label: 'Member programs', value: memberEventsCount },
          ].map((stat) => (
            <Paper
              key={stat.label}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid rgba(15, 118, 110, 0.12)',
                bgcolor: 'rgba(255, 255, 255, 0.82)',
                boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
              }}
            >
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                {stat.label}
              </Typography>
              <Typography sx={{ color: '#0f172a', fontWeight: 900, mt: 0.75 }}>
                {stat.value}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          borderRadius: 4,
          border: '1px solid rgba(15, 118, 110, 0.13)',
          boxShadow: '0 22px 70px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 900 }}>
              Upcoming events
            </Typography>
            <Typography sx={{ color: '#64748b', mt: 0.5 }}>
              Search by topic, venue, eligibility, or status and register from the event card.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchevents}
              sx={{ borderColor: '#0f766e', color: '#0f766e', fontWeight: 800 }}
            >
              Refresh
            </Button>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddEvent}
                sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#115e59' }, fontWeight: 800 }}
              >
                Add Event
              </Button>
            )}
          </Stack>
        </Box>

        <TextField
          label="Search events, venue, eligibility, or status"
          variant="outlined"
          fullWidth
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ mb: 2.5 }}
        />

        {lastSavedShareUrl && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              border: '1px solid #bfdbfe',
              borderRadius: 3,
              bgcolor: '#eff6ff',
            }}
          >
            <Typography sx={{ color: '#0f172a', fontWeight: 900, mb: 0.5 }}>
              Native registration / share link
            </Typography>
            <Typography variant="body2" sx={{ color: '#334155', wordBreak: 'break-all', mb: 1.5 }}>
              {lastSavedShareUrl}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                size="small"
                variant="contained"
                onClick={async () => {
                  await copyText(lastSavedShareUrl);
                }}
                sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#115e59' }, fontWeight: 900 }}
              >
                Copy link
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(lastSavedShareUrl, '_blank', 'noopener,noreferrer')}
                sx={{ fontWeight: 900 }}
              >
                Open registration link
              </Button>
            </Stack>
          </Paper>
        )}

        {notice.message && (
          <Alert severity={notice.type || 'info'} sx={{ mb: 2.5, borderRadius: 3 }}>
            {notice.message}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700 }}>
            Showing {sortedEvents.length} of {events.length} events
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              select
              label="Sort by"
              size="small"
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              sx={{ minWidth: 170 }}
              SelectProps={{ native: true }}
            >
              <option value="eventDate">Event date</option>
              <option value="startTime">Start time</option>
              <option value="eventTitle">Event title</option>
              <option value="venue">Venue</option>
              <option value="status">Status</option>
            </TextField>
            <Button
              variant="outlined"
              onClick={() => setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))}
              sx={{ borderColor: '#0f766e', color: '#0f766e', fontWeight: 900 }}
            >
              {sortDirection === 'asc' ? 'Ascending ↑' : 'Descending ↓'}
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {sortedEvents.length === 0 ? (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', textAlign: 'center' }}>
                <Typography sx={{ color: '#0f172a', fontWeight: 800 }}>No events found</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.75 }}>
                  Try a different search or refresh the event list.
                </Typography>
              </Paper>
            ) : (
              sortedEvents.map((event) => {
                const registrationUrl = getRegistrationUrl(event);
                const detailsUrl = buildEventDetailsUrl(event.id);
                const externalUrl = event.externalRegistrationUrl || event.link;
                const isPublished = (event.publicationStatus || 'published') === 'published';
                const shareUrl = isPublished ? (registrationUrl || externalUrl || detailsUrl) : '';
                const seatsLeft = event.capacity ? Math.max(0, Number(event.seatsLeft ?? event.capacity)) : null;
                const eventTitle = event.eventTitle || buildEventTitle(event.description);
                const eventSummary = compactText(event.description);
                const paymentSettings = event.paymentSettings || {};
                return (
                  <Paper
                    key={event.id}
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 2.25 },
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      bgcolor: '#ffffff',
                      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '160px minmax(0, 1fr) 220px' },
                        gap: 2,
                        alignItems: 'start',
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: '#0f172a', fontWeight: 950, lineHeight: 1.2 }}>
                          {formatCompactDate(event.eventDate)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 800, mt: 0.4 }}>
                          {formatEventDate(event.eventDate)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 900, mt: 1 }}>
                          {formatTimeRange(event)}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, mb: 1 }}>
                          <Chip
                            size="small"
                            label={event.eventCategory || 'Udyog Connect Event'}
                            sx={{ fontWeight: 900, bgcolor: '#eef5ff', color: '#1746d1' }}
                          />
                          <Chip
                            size="small"
                            label={event.status || 'TBA'}
                            color={isRegistrationOpen(event) ? 'success' : 'default'}
                            sx={{ fontWeight: 900 }}
                          />
                          <Chip
                            size="small"
                            label={(event.publicationStatus || 'published') === 'published' ? 'Published' : 'Draft'}
                            color={(event.publicationStatus || 'published') === 'published' ? 'primary' : 'warning'}
                            variant="outlined"
                            sx={{ fontWeight: 900 }}
                          />
                          <Chip
                            size="small"
                            label={event.whoCanApply || 'Anyone'}
                            variant="outlined"
                            sx={{ fontWeight: 900, bgcolor: '#f8fafc' }}
                          />
                          {event.capacity && (
                            <Chip
                              size="small"
                              label={`${seatsLeft} of ${event.capacity} seats left`}
                              sx={{ fontWeight: 900, bgcolor: '#dcfce7', color: '#166534' }}
                            />
                          )}
                          {paymentSettings.enabled && (
                            <Chip
                              size="small"
                              label={`$${Number(paymentSettings.amountUsd || 0).toFixed(2)}`}
                              sx={{ fontWeight: 900, bgcolor: '#ffedd5', color: '#9a3412' }}
                            />
                          )}
                        </Stack>
                        <Typography sx={{ color: '#0f172a', fontWeight: 950, lineHeight: 1.3 }}>
                          {eventTitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#475569',
                            lineHeight: 1.65,
                            mt: 0.75,
                            overflowWrap: 'anywhere',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {eventSummary}
                        </Typography>
                        {event.images?.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                            {event.images.slice(0, 4).map((image) => (
                              <Box
                                key={image.id}
                                component="img"
                                src={image.url}
                                alt="Event"
                                sx={{
                                  width: 92,
                                  height: 68,
                                  objectFit: 'cover',
                                  borderRadius: 2,
                                  border: '1px solid #dbeafe',
                                }}
                              />
                            ))}
                            {event.images.length > 4 && (
                              <Chip label={`+${event.images.length - 4} more`} size="small" sx={{ alignSelf: 'center', fontWeight: 900 }} />
                            )}
                          </Box>
                        )}
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 900, textTransform: 'uppercase' }}>
                          Venue
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 800, lineHeight: 1.45, mt: 0.25 }}>
                          {event.venue || 'Venue TBA'}
                        </Typography>
                      </Box>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                      {registrationUrl ? (
                        <Button
                          variant="contained"
                          disabled={!isRegistrationOpen(event)}
                          onClick={() => openRegistration(registrationUrl)}
                          sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#115e59' }, fontWeight: 900 }}
                        >
                          {isRegistrationOpen(event) ? 'Register / RSVP' : 'Registration Closed'}
                        </Button>
                      ) : (
                        <Button variant="outlined" disabled sx={{ fontWeight: 900 }}>
                          Registration link pending
                        </Button>
                      )}
                      {externalUrl && (
                        <Button
                          variant="outlined"
                          disabled={!isRegistrationOpen(event)}
                          onClick={() => openRegistration(externalUrl)}
                          sx={{ fontWeight: 900 }}
                        >
                          External link
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        disabled={!isPublished}
                        onClick={async () => {
                          await copyText(shareUrl);
                          setLastSavedShareUrl(shareUrl);
                          setNotice({ type: 'success', message: 'Native registration/share link copied.' });
                        }}
                        sx={{ fontWeight: 900 }}
                      >
                        Copy share link
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={!isPublished}
                        onClick={() => window.open(detailsUrl, '_blank', 'noopener,noreferrer')}
                        sx={{ fontWeight: 900 }}
                      >
                        Open details
                      </Button>
                      {isAdmin && (
                        <>
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditEvent(event)}>
                            Edit
                          </Button>
                          <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDeleteEvent(event.id)}>
                            Delete
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Paper>
                );
              })
            )}
          </Box>
        )}
      </Paper>

      {/* Modal for Adding/Editing Events */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="add-edit-event-modal"
        aria-describedby="modal-for-adding-or-editing-events"
      >
        <MuiBox
          sx={{
            position: 'absolute',
            top: { xs: '0', sm: '50%' },
            left: '50%',
            transform: { xs: 'translateX(-50%)', sm: 'translate(-50%, -50%)' },
            width: { xs: '100vw', sm: 680 },
            maxHeight: { xs: '100vh', sm: '90vh' },
            overflowY: 'auto',
            bgcolor: 'background.paper',
            border: { xs: 0, sm: '1px solid #e2e8f0' },
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            borderRadius: { xs: 0, sm: 3 },
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: '#0f172a', fontWeight: 900 }}>
            {editingEvent ? 'Edit Event' : 'Add Event'}
          </Typography>

          <TextField
            label="Event Name / Title"
            variant="outlined"
            fullWidth
            value={eventForm.eventTitle}
            onChange={(e) => setEventForm({ ...eventForm, eventTitle: e.target.value })}
            helperText="This appears as the public event name and registration page heading."
            sx={{ mb: 2 }}
          />
          <TextField
            label="Category"
            variant="outlined"
            fullWidth
            value={eventForm.eventCategory}
            onChange={(e) => setEventForm({ ...eventForm, eventCategory: e.target.value })}
            helperText="Example: Udyog Connect Event, Volunteer Event, Student Internship Session."
            sx={{ mb: 2 }}
          />

          <TextField
            label="Event Date"
            variant="outlined"
            fullWidth
            type="date"
            value={eventForm.eventDate}
            onChange={(e) => setEventForm({ ...eventForm, eventDate: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Start Time"
            variant="outlined"
            fullWidth
            type="time"
            value={eventForm.startTime}
            onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
            sx={{ mb: 2 }}
          />

          <TextField
            label="End Time"
            variant="outlined"
            fullWidth
            type="time"
            value={eventForm.endTime}
            onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#475569', fontSize: '0.8rem', fontWeight: 900, mb: 1, textTransform: 'uppercase' }}>
              Rich event content
            </Typography>
            <ReactQuill
              theme="snow"
              value={eventForm.description}
              onChange={(value) => setEventForm({ ...eventForm, description: value })}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline'],
                  [{ color: [] }, { background: [] }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  [{ align: [] }],
                  ['link', 'clean'],
                ],
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 1 }}>
              Use headings, bold text, lists, links, and colors. This will render like a rich SignupGenius-style event page.
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              mb: 2,
              border: '1px dashed #bfdbfe',
              borderRadius: 2,
              bgcolor: '#f8fbff',
            }}
          >
            <Typography sx={{ color: '#0f172a', fontWeight: 900, mb: 0.75 }}>
              Event images
            </Typography>
            {existingImages.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {existingImages.map((image) => (
                  <Box key={image.id} sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={image.url}
                      alt="Existing event"
                      sx={{
                        width: 92,
                        height: 70,
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '1px solid #dbeafe',
                      }}
                    />
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      onClick={() => deleteExistingImage(image.id)}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        minWidth: 26,
                        width: 26,
                        height: 26,
                        p: 0,
                        borderRadius: '50%',
                      }}
                    >
                      ×
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
            {selectedImagePreviews.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {selectedImagePreviews.map((image) => (
                  <Box
                    key={image.name}
                    component="img"
                    src={image.url}
                    alt={image.name}
                    sx={{
                      width: 92,
                      height: 70,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '1px solid #bbf7d0',
                    }}
                  />
                ))}
              </Box>
            )}
            <Button variant="outlined" component="label" sx={{ fontWeight: 900 }}>
              Choose images
              <input
                hidden
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setSelectedImages(Array.from(e.target.files || []))}
              />
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 1 }}>
              You can select multiple images. They will upload when you save the event.
            </Typography>
          </Box>
          <TextField
            label="Venue"
            variant="outlined"
            fullWidth
            value={eventForm.venue}
            onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="External Registration Link (optional)"
            variant="outlined"
            fullWidth
            value={eventForm.link}
            onChange={(e) => setEventForm({ ...eventForm, link: e.target.value })}
            helperText="A native UC registration link is generated automatically. Use this only if another platform is needed."
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField
              label="Seat Capacity"
              type="number"
              inputProps={{ min: 0 }}
              value={eventForm.capacity}
              onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })}
              helperText="Leave blank for unlimited/open registration."
            />
            <TextField
              select
              label="Native UC Registration"
              value={String(eventForm.nativeRegistrationEnabled)}
              onChange={(e) => setEventForm({ ...eventForm, nativeRegistrationEnabled: e.target.value === 'true' })}
              SelectProps={{ native: true }}
              helperText="Keep enabled to auto-generate /2.0/event/{id}/register."
            >
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </TextField>
          </Box>

          <Box
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid #fde68a',
              borderRadius: 2,
              bgcolor: '#fffaf0',
              display: 'grid',
              gap: 1.5,
            }}
          >
            <Typography sx={{ color: '#7c2d12', fontWeight: 950 }}>
              Payment-ready settings
            </Typography>
            <TextField
              select
              label="Registration fee"
              value={String(eventForm.paymentSettings.enabled)}
              onChange={(e) => updatePaymentSetting('enabled', e.target.value === 'true')}
              SelectProps={{ native: true }}
              helperText="Real checkout is not enabled yet; this prepares the event for future paid registration."
            >
              <option value="false">Free event</option>
              <option value="true">Paid registration / payment follow-up</option>
            </TextField>
            {eventForm.paymentSettings.enabled && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  <TextField
                    label="Dollar amount"
                    type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    value={eventForm.paymentSettings.amountUsd}
                    onChange={(e) => updatePaymentSetting('amountUsd', e.target.value)}
                  />
                  <TextField
                    label="Payment label"
                    value={eventForm.paymentSettings.label}
                    onChange={(e) => updatePaymentSetting('label', e.target.value)}
                    placeholder="General Admission"
                  />
                </Box>
                <TextField
                  select
                  label="Show add-to-cart placeholder"
                  value={String(eventForm.paymentSettings.addToCartEnabled)}
                  onChange={(e) => updatePaymentSetting('addToCartEnabled', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </TextField>
                <TextField
                  label="Payment note"
                  multiline
                  minRows={2}
                  value={eventForm.paymentSettings.note}
                  onChange={(e) => updatePaymentSetting('note', e.target.value)}
                />
              </>
            )}
          </Box>

          <Box
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid #bfdbfe',
              borderRadius: 2,
              bgcolor: '#f8fbff',
              display: 'grid',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ color: '#0f172a', fontWeight: 950 }}>
                  Registration form builder
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Add future fields and mark them mandatory or optional for this event.
                </Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={addRegistrationField} sx={{ fontWeight: 900 }}>
                Add field
              </Button>
            </Box>
            {eventForm.registrationFields.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Default required fields are Full Name, Email, and Consent. Add custom fields only when needed.
              </Alert>
            ) : (
              eventForm.registrationFields.map((field, index) => (
                <Paper key={field.key || index} elevation={0} sx={{ p: 1.5, border: '1px solid #dbeafe', borderRadius: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.7fr 0.65fr auto' }, gap: 1.25, alignItems: 'start' }}>
                    <TextField
                      label="Field label"
                      value={field.label}
                      onChange={(e) => updateRegistrationField(index, 'label', e.target.value)}
                      placeholder="Example: T-shirt size"
                    />
                    <TextField
                      select
                      label="Field type"
                      value={field.type}
                      onChange={(e) => updateRegistrationField(index, 'type', e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      {registrationFieldTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Required?"
                      value={String(field.required)}
                      onChange={(e) => updateRegistrationField(index, 'required', e.target.value === 'true')}
                      SelectProps={{ native: true }}
                    >
                      <option value="false">Optional</option>
                      <option value="true">Mandatory</option>
                    </TextField>
                    <Button color="error" variant="outlined" onClick={() => removeRegistrationField(index)} sx={{ minHeight: 56 }}>
                      Remove
                    </Button>
                  </Box>
                  <TextField
                    label="Placeholder / helper text"
                    fullWidth
                    value={field.placeholder}
                    onChange={(e) => updateRegistrationField(index, 'placeholder', e.target.value)}
                    sx={{ mt: 1.25 }}
                  />
                  {field.type === 'select' && (
                    <TextField
                      label="Dropdown options"
                      fullWidth
                      multiline
                      minRows={2}
                      value={field.optionsText}
                      onChange={(e) => updateRegistrationField(index, 'optionsText', e.target.value)}
                      helperText="One option per line, or comma-separated."
                      sx={{ mt: 1.25 }}
                    />
                  )}
                </Paper>
              ))
            )}
          </Box>
          <TextField
            select
            label="Event Status"
            variant="outlined"
            fullWidth
            value={eventForm.status}
            onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })}
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
          >
            <option value=""></option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </TextField>
          <TextField
            select
            label="Publication"
            variant="outlined"
            fullWidth
            value={eventForm.publicationStatus}
            onChange={(e) => setEventForm({ ...eventForm, publicationStatus: e.target.value })}
            sx={{ mb: 2 }}
            helperText="Draft is admin-only. Publish when ready for visitors and registration."
            SelectProps={{ native: true }}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </TextField>
          <TextField
            select
            label="Who Can Apply"
            variant="outlined"
            fullWidth
            value={eventForm.whoCanApply}
            onChange={(e) => setEventForm({ ...eventForm, whoCanApply: e.target.value })}
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
          >
            <option value=""></option>
            <option value="anyone">Anyone</option>
            <option value="member">Members Only</option>
          </TextField>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setOpen(false)}
              sx={{ fontWeight: 800 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleFormSubmit}
              disabled={savingEvent}
              sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#115e59' }, fontWeight: 800 }}
            >
              {savingEvent ? 'Saving…' : editingEvent ? 'Update Event' : 'Add Event'}
            </Button>
          </Box>
        </MuiBox>
      </Modal>
    </Container>
    </Box>
  );
};

export default Events;
